import React, { useRef } from "react";
import { motion, useDragControls, PanInfo } from "framer-motion";

interface MainDialProps {
  rotation: number;
  onRotate: (degrees: number) => void;
  onRelease?: () => void;
  className?: string;
}

export const MainDial: React.FC<MainDialProps> = ({ rotation, onRotate, onRelease, className }) => {
  const dialRef = useRef<HTMLDivElement>(null);
  const controls = useDragControls();

  const handleDrag = (event: any, info: PanInfo) => {
    if (!dialRef.current) return;
    const rect = dialRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    // Calculate angle based on mouse/touch position
    const x = info.point.x - centerX;
    const y = info.point.y - centerY;
    
    // Atan2 returns radians, convert to degrees (90deg offset for top-center)
    let angle = Math.atan2(y, x) * (180 / Math.PI);
    angle = angle + 90; 

    onRotate(angle);
  };

  return (
    <div className={`relative w-64 h-64 flex items-center justify-center ${className}`} ref={dialRef}>
      <motion.div
        drag
        dragControls={controls}
        dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }} // Lock position, only rotate
        dragElastic={0}
        dragMomentum={false}
        onDrag={handleDrag}
        onDragEnd={onRelease}
        animate={{ rotate: rotation }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="w-full h-full cursor-grab active:cursor-grabbing touch-none"
        style={{ touchAction: "none" }} 
      >
        <img 
          src="/images/dial.png" 
          alt="Navigator Dial" 
          className="w-full h-full object-contain drop-shadow-2xl pointer-events-none select-none" 
          onError={(e) => {
            // Fallback just in case image is missing momentarily
            e.currentTarget.style.display = 'none';
            e.currentTarget.parentElement!.style.background = 'radial-gradient(circle, #334155, #0f172a)';
            e.currentTarget.parentElement!.style.borderRadius = '50%';
          }}
        />
      </motion.div>
    </div>
  );
};